import { workflowApi } from './api';

export type SuggestConfig = {
  url?: string;
  provider?: 'auth0' | 'entra' | string;
  kind?: 'users' | 'groups' | string;
  min_chars?: number;
  limit?: number;
};

export type UiConfig = {
  debounceMs?: number;
  normalizeOn?: 'blur' | 'submit' | 'both' | 'none';
};

export type ValidationConfig = {
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  patternMessage?: string;
};

export type FormField = {
  name: string;
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  label?: string;
  description?: string;
  placeholder?: string;
  required?: boolean;
  enum?: Array<string>;
  redact?: boolean;
  regexPattern?: string;
  validation?: ValidationConfig;
  suggest?: SuggestConfig;
  ui?: UiConfig;
};

export type FormSchemaUI = {
  title?: string;
  fields: FormField[];
};

export type CommandKey = { system: string; objectType: string; action: string };

const cache = new Map<string, FormSchemaUI>();

function keyOf(k: CommandKey): string {
  return `${k.system}::${k.objectType}::${k.action}`;
}

export function mapCrudSchemaToUi(crudSchema: any): FormSchemaUI {
  // Case A: backend already returns UI shape {fields, ui, title}
  if (crudSchema && Array.isArray(crudSchema.fields)) {
    const fields = (crudSchema.fields as any[]).map((f) => ({
      name: String(f.name),
      type: (f.type as any) || 'string',
      label: f.label || f.title || f.name,
      description: f.description,
      placeholder: f.placeholder,
      required: !!f.required,
      enum: Array.isArray(f.enum) ? f.enum : undefined,
      redact: !!f.redact,
      regexPattern: typeof f.regexPattern === 'string' ? f.regexPattern : (typeof f.pattern === 'string' ? f.pattern : undefined),
      validation: f.validation,
      suggest: f.suggest,
      ui: f.ui,
    })) as FormField[];
    return { title: crudSchema.title, fields };
  }

  // Case B: JSON Schema → map to UI
  const out: FormSchemaUI = { title: crudSchema?.title, fields: [] };
  const props = (crudSchema?.properties && typeof crudSchema.properties === 'object') ? crudSchema.properties : {};
  const required = Array.isArray(crudSchema?.required) ? (crudSchema.required as string[]) : [];

  const entries = Object.entries(props) as Array<[string, any]>;
  for (const [name, meta] of entries) {
    if (!meta || typeof meta !== 'object') continue;
    const field: FormField = {
      name,
      type: (meta.type as any) || 'string',
      label: meta.title || name,
      description: meta.description || undefined,
      placeholder: meta.placeholder || undefined,
      required: required.includes(name),
      enum: Array.isArray(meta.enum) ? meta.enum : undefined,
      redact: !!(meta['x-redact'] || meta['x_redact'] || meta.redact),
      regexPattern: typeof meta.pattern === 'string' ? meta.pattern : undefined,
      validation: {
        minLength: typeof meta.minLength === 'number' ? meta.minLength : undefined,
        maxLength: typeof meta.maxLength === 'number' ? meta.maxLength : undefined,
        pattern: typeof meta.pattern === 'string' ? meta.pattern : undefined,
        patternMessage: typeof meta.patternMessage === 'string' ? meta.patternMessage : undefined,
      },
      suggest: mapSuggest(meta['x-suggest'] || meta['x_suggest']),
      ui: mapUi(meta['x-ui'] || meta['x_ui']),
    };
    out.fields.push(field);
  }
  // Keep fields stable ordering
  out.fields.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

function mapSuggest(xs: any): SuggestConfig | undefined {
  if (!xs || typeof xs !== 'object') return undefined;
  const cfg: SuggestConfig = {};
  if (typeof xs.url === 'string') cfg.url = xs.url;
  if (typeof xs.provider === 'string') cfg.provider = xs.provider as any;
  if (typeof xs.kind === 'string') cfg.kind = xs.kind as any;
  if (typeof xs.min_chars === 'number') cfg.min_chars = xs.min_chars;
  if (typeof xs.limit === 'number') cfg.limit = xs.limit;
  return cfg;
}

function mapUi(xui: any): UiConfig | undefined {
  if (!xui || typeof xui !== 'object') return undefined;
  const u: UiConfig = {};
  if (typeof xui.debounceMs === 'number') u.debounceMs = xui.debounceMs;
  const norm = xui.normalizeOn;
  if (norm === 'blur' || norm === 'submit' || norm === 'both' || norm === 'none') u.normalizeOn = norm;
  return u;
}

export async function fetchFormSchemaUI(key: CommandKey): Promise<FormSchemaUI> {
  const ck = keyOf(key);
  if (cache.has(ck)) return cache.get(ck)!;
  const resp: any = await workflowApi.formSchema(key.system, key.objectType, key.action);
  // The backend may return JSON Schema or UI shape; normalize
  const crudSchema = resp?.schema ?? resp;
  const mapped = mapCrudSchemaToUi(crudSchema);
  cache.set(ck, mapped);
  return mapped;
}

export function clearFormSchemaCache(): void {
  cache.clear();
}
